/**
 * System Validation Utilities
 * Centralized validation functions to prevent common user errors
 * and provide helpful feedback throughout the application
 */

import type { Rule } from 'antd/es/form';
import dayjs, { Dayjs } from 'dayjs';

import { toast } from './toast';

/**
 * Validation result interface
 */
export interface SystemValidationResult {
  isValid: boolean;
  message?: string;
}

/**
 * Validate that a group has products configured
 * @param products - Array of products to validate
 * @param context - Context description (e.g., "policy capture", "funeral invoice")
 * @returns ValidationResult with user-friendly message
 */
export const validateGroupHasProducts = (
  products: any[] | null | undefined,
  context: string = 'this operation'
): SystemValidationResult => {
  if (!products || products.length === 0) {
    const message = `No products are configured for this group. Please set up products before attempting ${context}.`;
    
    toast.warning({
      title: 'Products Required',
      description: message,
      duration: 8,
    });

    return {
      isValid: false,
      message,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validate that a group has funeral products configured
 * @param products - Array of funeral products to validate
 * @returns ValidationResult with user-friendly message
 */
export const validateGroupHasFuneralProducts = (
  products: any[] | null | undefined
): SystemValidationResult => {
  if (!products || products.length === 0) {
    const message =
      'No funeral products are configured for this group. Please set up funeral products before creating invoices.';

    toast.warning({
      title: 'Funeral Products Required',
      description: message,
      duration: 8,
    });

    return {
      isValid: false,
      message,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validate that a member has a payment method configured
 * @param member - Member object to validate
 * @returns ValidationResult with user-friendly message
 */
export const validateMemberHasPaymentMethod = (
  member: any
): SystemValidationResult => {
  if (!member?.PaymentType) {
    const message =
      'This member does not have a payment method configured. Please add a payment method before processing transactions.';

    toast.warning({
      title: 'Payment Method Required',
      description: message,
      duration: 6,
    });

    return {
      isValid: false,
      message,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validate that a group has bank details configured for payments
 * @param group - Group object to validate
 * @returns ValidationResult with user-friendly message
 */
export const validateGroupHasBankDetails = (
  group: any
): SystemValidationResult => {
  if (!group?.BankName || !group?.AccountNumber || !group?.BranchCode) {
    const message =
      'This group does not have complete banking details configured. Please update the group settings before processing bulk payments.';

    toast.warning({
      title: 'Banking Details Required',
      description: message,
      duration: 7,
    });

    return {
      isValid: false,
      message,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validate that DebiCheck is properly configured for a member
 * @param member - Member object to validate
 * @returns ValidationResult with user-friendly message
 */
export const validateMemberDebiCheckSetup = (
  member: any
): SystemValidationResult => {
  if (!member?.PaymentType?.DebiCheckEnabled) {
    const message =
      'DebiCheck is not enabled for this member. Please configure DebiCheck before performing mandate operations.';

    toast.info({
      title: 'DebiCheck Not Configured',
      description: message,
      duration: 6,
    });

    return {
      isValid: false,
      message,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validate that a policy number is in the correct format
 * @param policyNumber - Policy number to validate
 * @returns ValidationResult with user-friendly message
 */
export const validatePolicyNumberFormat = (
  policyNumber: string
): SystemValidationResult => {
  if (!policyNumber || policyNumber.length !== 15) {
    const message = 'Policy number must be exactly 15 characters long.';

    toast.error({
      title: 'Invalid Policy Number',
      description: message,
      duration: 5,
    });

    return {
      isValid: false,
      message,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validate that required fields are present before form submission
 * @param fields - Object containing field names and values
 * @param requiredFields - Array of required field names
 * @returns ValidationResult with user-friendly message
 */
export const validateRequiredFields = (
  fields: Record<string, any>,
  requiredFields: string[]
): SystemValidationResult => {
  const missingFields = requiredFields.filter(
    (field) => !fields[field] || fields[field] === ''
  );

  if (missingFields.length > 0) {
    const fieldList = missingFields.join(', ');
    const message = `The following required fields are missing: ${fieldList}`;

    toast.error({
      title: 'Required Fields Missing',
      description: message,
      duration: 6,
    });

    return {
      isValid: false,
      message,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validate that a date is within an acceptable range
 * @param date - Date to validate
 * @param minDate - Minimum acceptable date
 * @param maxDate - Maximum acceptable date
 * @param fieldName - Name of the field being validated
 * @returns ValidationResult with user-friendly message
 */
export const validateDateRange = (
  date: Date,
  minDate: Date,
  maxDate: Date,
  fieldName: string = 'Date'
): SystemValidationResult => {
  if (date < minDate || date > maxDate) {
    const message = `${fieldName} must be between ${minDate.toLocaleDateString()} and ${maxDate.toLocaleDateString()}.`;

    toast.error({
      title: 'Invalid Date',
      description: message,
      duration: 5,
    });

    return {
      isValid: false,
      message,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Returns true when the date is the 1st day of its month.
 */
export const isFirstOfMonth = (
  date: Dayjs | Date | string | null | undefined
): boolean => {
  if (date == null) return false;
  const parsed = dayjs(date);
  return parsed.isValid() && parsed.date() === 1;
};

/**
 * Earliest selectable inception month: 1st of the previous calendar month.
 */
export const earliestAllowedInceptionMonth = (
  now: Dayjs = dayjs()
): Dayjs => now.startOf("month").subtract(1, "month");

/** Previous month, current month, or any future month. */
export const isInceptionMonthAllowed = (
  date: Dayjs | Date | string | null | undefined,
  now: Dayjs = dayjs()
): boolean => {
  if (!isFirstOfMonth(date)) return false;
  const parsed = dayjs(date);
  if (!parsed.isValid()) return false;
  return !parsed.isBefore(earliestAllowedInceptionMonth(now), "month");
};

/** Disable months older than the previous month in Ant Design month pickers. */
export const disabledInceptionMonth = (
  current: Dayjs | null,
  now: Dayjs = dayjs()
): boolean => {
  if (!current) return false;
  return current
    .startOf("month")
    .isBefore(earliestAllowedInceptionMonth(now), "month");
};

/**
 * Normalizes an inception date to the 1st of the selected month.
 */
export const normalizeInceptionDate = (
  value: Dayjs | Date | string | null | undefined
): Dayjs | undefined => {
  if (value == null) return undefined;
  const parsed = dayjs(value);
  if (!parsed.isValid()) return undefined;
  return parsed.startOf("month");
};

/**
 * Ant Design form rule — inception must be 1st of month (add/edit member).
 */
export const inceptionDateFormRule: Rule = {
  validator: (_, value) => {
    if (!value) return Promise.resolve();
    if (!isFirstOfMonth(value)) {
      return Promise.reject(
        new Error("Inception date must be the 1st of the month")
      );
    }
    return Promise.resolve();
  },
};

/**
 * Activate-lapsed only: 1st of month within previous/current/future window.
 */
export const activateInceptionDateFormRule: Rule = {
  validator: (_, value) => {
    if (!value) return Promise.resolve();
    if (!isFirstOfMonth(value)) {
      return Promise.reject(
        new Error("Inception date must be the 1st of the month")
      );
    }
    if (!isInceptionMonthAllowed(value)) {
      return Promise.reject(
        new Error(
          "Inception date can only be the previous month, the current month, or a future month"
        )
      );
    }
    return Promise.resolve();
  },
};

/** SA mobile/telephone: 0821234567 or +27821234567 */
export const SA_CELLPHONE_PATTERN = /^(?:\+27|0)[1-9]\d{8}$/;

/** Digits only, with +27 normalized to leading 0. */
export const normalizeSACellphoneDigits = (
  value: string | null | undefined
): string => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  let digits = raw.replace(/\D/g, "");
  if (digits.startsWith("27") && digits.length >= 11) {
    digits = `0${digits.slice(2)}`;
  }
  return digits;
};

/** More than six trailing zeros (placeholder / fake numbers). */
export const hasSuspiciousTrailingZeros = (
  value: string | null | undefined
): boolean => {
  const digits = normalizeSACellphoneDigits(value);
  return digits.length > 0 && /0{7,}$/.test(digits);
};

/**
 * Returns true when value matches SA cellphone/telephone format
 * and does not end with more than six zeros.
 */
export const isValidSACellphone = (
  value: string | null | undefined
): boolean => {
  if (value == null || String(value).trim() === "") return false;
  const trimmed = String(value).trim();
  if (!SA_CELLPHONE_PATTERN.test(trimmed)) return false;
  if (hasSuspiciousTrailingZeros(trimmed)) return false;
  return true;
};

export type CellphoneIssueKind = "missing" | "format" | "trailingZeros";

export const getCellphoneIssueKind = (
  value: string | null | undefined
): CellphoneIssueKind | null => {
  if (value == null || String(value).trim() === "") return "missing";
  const trimmed = String(value).trim();
  if (hasSuspiciousTrailingZeros(trimmed)) return "trailingZeros";
  if (!SA_CELLPHONE_PATTERN.test(trimmed)) return "format";
  return null;
};

/** Aligns with BE compliance Detail enums: Missing | Invalid format | Suspicious trailing zeros */
export const getCellphoneIssueDetail = (
  value: string | null | undefined
): string => {
  const kind = getCellphoneIssueKind(value);
  if (kind === "missing") return "Missing";
  if (kind === "trailingZeros") return "Suspicious trailing zeros";
  if (kind === "format") return "Invalid format";
  return "";
};

/**
 * Ant Design form rule — required SA cellphone.
 */
export const saCellphoneFormRule: Rule = {
  required: true,
  validator: async (_, value) => {
    if (value == null || String(value).trim() === "") {
      return Promise.reject(
        new Error("Please enter a valid SA cellphone number (e.g. 0821234567)")
      );
    }
    if (hasSuspiciousTrailingZeros(value)) {
      return Promise.reject(
        new Error(
          "Cellphone cannot end with more than six zeros (e.g. 0820000000)"
        )
      );
    }
    if (!SA_CELLPHONE_PATTERN.test(String(value).trim())) {
      return Promise.reject(
        new Error("Please enter a valid SA cellphone number (e.g. 0821234567)")
      );
    }
    return Promise.resolve();
  },
};

/**
 * Ant Design form rule — optional SA telephone (same format when provided).
 */
export const saTelephoneFormRule: Rule = {
  validator: async (_, value) => {
    if (value == null || String(value).trim() === "") return Promise.resolve();
    if (hasSuspiciousTrailingZeros(value)) {
      return Promise.reject(
        new Error(
          "Telephone cannot end with more than six zeros (e.g. 0820000000)"
        )
      );
    }
    if (!SA_CELLPHONE_PATTERN.test(String(value).trim())) {
      return Promise.reject(
        new Error("Please enter a valid SA telephone number (e.g. 0821234567)")
      );
    }
    return Promise.resolve();
  },
};

/**
 * Validate SA cellphone with toast feedback (e.g. before policy activation).
 */
export const validateSACellphone = (
  value: string | null | undefined
): SystemValidationResult => {
  if (!isValidSACellphone(value)) {
    const message =
      'This member has an invalid cellphone number. Edit the member and enter a valid SA number (e.g. 0821234567) before activating.';

    toast.error({
      title: 'Invalid Cellphone',
      description: message,
      duration: 6,
    });

    return { isValid: false, message };
  }

  return { isValid: true };
};

/**
 * Validate that a member's age is within product limits
 * @param age - Member's age
 * @param minAge - Minimum age for product
 * @param maxAge - Maximum age for product
 * @returns ValidationResult with user-friendly message
 */
export const validateMemberAge = (
  age: number,
  minAge: number,
  maxAge: number
): SystemValidationResult => {
  if (age < minAge || age > maxAge) {
    const message = `Member age (${age}) is outside the acceptable range for this product (${minAge}-${maxAge} years).`;

    toast.error({
      title: 'Age Not Eligible',
      description: message,
      duration: 6,
    });

    return {
      isValid: false,
      message,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validate that user has necessary permissions for an action
 * @param userRole - Current user's role
 * @param allowedRoles - Array of roles permitted to perform the action
 * @param actionName - Name of the action being attempted
 * @returns ValidationResult with user-friendly message
 */
export const validateUserPermission = (
  userRole: string,
  allowedRoles: string[],
  actionName: string = 'this action'
): SystemValidationResult => {
  if (!allowedRoles.includes(userRole)) {
    const message = `You do not have permission to perform ${actionName}. Required roles: ${allowedRoles.join(', ')}`;

    toast.error({
      title: 'Permission Denied',
      description: message,
      duration: 6,
    });

    return {
      isValid: false,
      message,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validate that an ID number is in the correct South African format
 * @param idNumber - ID number to validate
 * @returns ValidationResult with user-friendly message
 */
export const validateSAIdNumber = (idNumber: string): SystemValidationResult => {
  // Basic SA ID number validation (13 digits)
  const idPattern = /^\d{13}$/;

  if (!idPattern.test(idNumber)) {
    const message =
      'Invalid South African ID number. Must be exactly 13 digits.';

    toast.error({
      title: 'Invalid ID Number',
      description: message,
      duration: 5,
    });

    return {
      isValid: false,
      message,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Validate that selected members have outstanding balances
 * @param members - Array of member objects
 * @returns ValidationResult with user-friendly message
 */
export const validateMembersHaveOutstanding = (
  members: any[]
): SystemValidationResult => {
  if (!members || members.length === 0) {
    const message = 'No members selected for payment processing.';

    toast.warning({
      title: 'No Members Selected',
      description: message,
      duration: 5,
    });

    return {
      isValid: false,
      message,
    };
  }

  const membersWithNoBalance = members.filter(
    (member) => !member.OutstandingAmount || member.OutstandingAmount <= 0
  );

  if (membersWithNoBalance.length === members.length) {
    const message = 'None of the selected members have outstanding balances.';

    toast.info({
      title: 'No Outstanding Balances',
      description: message,
      duration: 5,
    });

    return {
      isValid: false,
      message,
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Show a generic error message for failed API calls
 * @param error - Error object from API call
 * @param context - Context description of what failed
 */
export const showApiErrorMessage = (error: any, context: string): void => {
  const message =
    error?.response?.data?.message ||
    error?.message ||
    `An unexpected error occurred while ${context}. Please try again or contact support if the issue persists.`;

  toast.error({
    title: 'Operation Failed',
    description: message,
    duration: 7,
  });
};

/**
 * Show a success message for completed operations
 * @param context - Context description of what succeeded
 * @param details - Optional additional details
 */
export const showSuccessMessage = (
  context: string,
  details?: string
): void => {
  toast.success({
    title: 'Success',
    description: details || `${context} completed successfully.`,
    duration: 4,
  });
};
